import React, { useEffect, useState } from "react";
import { AppPermission, CalendarInfo } from "@closerplatform/spinner-openapi";
import { observer } from "mobx-react";
import { useStore } from "../../../models";
import { AutoComplete } from "@fidget/common/components/form/autoComplete/autoComplete";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { SubtitleDescription } from "../subtitleDescription";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import styles from "./googleSyncSettings.css";
import { withPermissionFallback } from "../../HOC/withPermissionFallback/withPermissionFallback";

enum ImportExportOption {
  IMPORT_ONLY = "importOnly",
  EXPORT_ONLY = "exportOnly",
  IMPORT_EXPORT = "importExport"
}

export const GoogleSyncSettings = withPermissionFallback(
  AppPermission.SettingsPersonal,
  observer(() => {
    const { calendarStore, currentUserStore } = useStore();
    const [isEditing, setIsEditing] = useState(false);
    const [isSignedToGoogle, setIsSingedToGoogle] = useState<boolean>();
    const [calendars, setCalendars] = useState<Array<CalendarInfo>>([]);
    const [selectedCalendar, setSelectedCalendar] = useState<CalendarInfo>();

    useEffect(() => {
      const { profile } = currentUserStore;
      if (!profile) {
        return;
      }

      const { importedCalendar } = profile;
      calendarStore
        .getCalendarsFromGoogle()
        .then((fetchedCalendars) => {
          const currentCalendar = importedCalendar
            ? fetchedCalendars.find((calendar) => calendar.title === importedCalendar)
            : undefined;

          setCalendars(fetchedCalendars);
          setIsSingedToGoogle(true);
          setSelectedCalendar(currentCalendar);
        })
        .catch(() => {
          if (importedCalendar) {
            stopSync();
          }
          setIsSingedToGoogle(false);
        });
    }, []);

    const startEditing = () => {
      setIsEditing(true);
      setSelectedCalendar(calendars[0]);
    };

    const stopEditing = () => {
      setIsEditing(false);
      setSelectedCalendar(undefined);
    };

    const googleSignIn = async () => {
      const fetchedCalendars = await calendarStore.googleSignIn();
      setIsSingedToGoogle(true);
      setIsEditing(true);
      setCalendars(fetchedCalendars);
      setSelectedCalendar(fetchedCalendars[0]);
    };

    const saveCalendar = async () => {
      if (selectedCalendar && currentUserStore.profile) {
        const { id, title } = selectedCalendar;
        await calendarStore.setImportedCalendar({ calendarId: id, calendarName: title });
        currentUserStore.profile.setImportedCalendar(title);
        setIsEditing(false);
      }
    };

    const stopSync = async () => {
      await calendarStore.stopSyncGoogleCalendar();
      setSelectedCalendar(undefined);
      setIsEditing(false);
    };

    const importExportToLabel = (option: ImportExportOption) => {
      switch (option) {
        case ImportExportOption.IMPORT_ONLY:
          return intl.formatMessage("settings.settingsGoogle.importOnly");
        case ImportExportOption.EXPORT_ONLY:
          return intl.formatMessage("settings.settingsGoogle.exportOnly");
        case ImportExportOption.IMPORT_EXPORT:
          return intl.formatMessage("settings.settingsGoogle.importExport");
        default:
          return "";
      }
    };

    const importExportToDescription = (option: ImportExportOption) => {
      switch (option) {
        case ImportExportOption.IMPORT_ONLY:
          return intl.formatMessage(`settings.settingsGoogle.importOnly.description`);
        case ImportExportOption.EXPORT_ONLY:
          return intl.formatMessage("settings.settingsGoogle.exportOnly.description");
        case ImportExportOption.IMPORT_EXPORT:
          return intl.formatMessage("settings.settingsGoogle.importExport.description");
        default:
          return "";
      }
    };

    const mapInfoToString = (info: CalendarInfo | null) => info?.title || "";

    const renderImportExportOptions = () => {
      const options = [
        ImportExportOption.IMPORT_ONLY,
        ImportExportOption.EXPORT_ONLY,
        ImportExportOption.IMPORT_EXPORT
      ];

      return (
        <div className={commonStyles["import-export-container"]}>
          {options.map((option) => (
            <label key={option} className={commonStyles.option} htmlFor={option}>
              <input
                type="radio"
                id={option}
                name={"importExport"}
                value={option}
                checked={option === ImportExportOption.IMPORT_ONLY}
                disabled={option !== ImportExportOption.IMPORT_ONLY}
                readOnly
              />
              <div className={commonStyles["coming-soon"]}>{intl.formatMessage("comingSoon")}</div>
              <div className={commonStyles.label}>{importExportToLabel(option)} </div>
              <div className={commonStyles.description}>{importExportToDescription(option)}</div>
            </label>
          ))}
        </div>
      );
    };

    const render = () => {
      const { profile } = currentUserStore;
      if (!profile) {
        return <></>;
      }
      const { importedCalendar } = profile;

      return (
        <>
          <div className={commonStyles.title}>
            <L10n id={"settings.settingsGoogle"} />
          </div>

          <div className={commonStyles.section}>
            <SubtitleDescription
              subtitle={intl.formatMessage("settings.settingsGoogle.syncCalendar")}
              description={intl.formatMessage("settings.settingsGoogle.syncCalendar.description")}
            />
            <div className={commonStyles.content}>
              {!isSignedToGoogle || (!importedCalendar && !isEditing) ? (
                <AgentButton
                  onClick={!isSignedToGoogle ? googleSignIn : startEditing}
                  disabled={isSignedToGoogle === undefined}
                >
                  <L10n id={"settings.settingsGoogle.sync"} />
                </AgentButton>
              ) : (
                <>
                  <AgentButton
                    buttonStyle={"outlined red"}
                    onClick={importedCalendar ? stopSync : stopEditing}
                  >
                    <L10n id={"settings.settingsGoogle.stop"} />
                  </AgentButton>
                  {!!calendars.length && selectedCalendar && (
                    <div className={commonStyles["calendars-select-container"]}>
                      <AutoComplete<CalendarInfo>
                        className={styles["calendar-picker"]}
                        readOnly
                        itemsProvider={() => calendars}
                        onChange={(item) => setSelectedCalendar(item as CalendarInfo)}
                        itemToString={mapInfoToString}
                        inputValue={mapInfoToString(selectedCalendar)}
                        autoFocus
                      />
                      {selectedCalendar.title !== importedCalendar && (
                        <>
                          {renderImportExportOptions()}
                          <AgentButton onClick={saveCalendar}>
                            {intl.formatMessage("settings.apply")}
                          </AgentButton>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      );
    };

    return render();
  })
);
